import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthenService } from '@core/authen/authen.service';
import { EventPaymentService } from '@core/http/event-payment.service';
import { SessionService } from '@core/session.service';
import { ToastService } from '@core/toast.service';
import { environment } from '@env/environment';
import { TranslateModule } from '@ngx-translate/core';
import { StorageEnum } from '@shared/enums/storage.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { EventInterface } from '@shared/interfaces/event.interface';
import { TwoFactorVerificationPayload } from '@shared/interfaces/request/authen.interface';
import { StringReplacementPipe } from '@shared/pipes/string-replacement.pipe';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-form-two-factor',
  templateUrl: './form-two-factor.component.html',
  imports: [
    ReactiveFormsModule,
    StringReplacementPipe,
    TranslateModule,
    CommonModule,
  ],
})
export class FormTwoFactorComponent implements OnInit, OnDestroy {
  @Input() credentialOTP: TwoFactorVerificationPayload;
  @Input() eventId;
  joiningEvent = input<boolean>(false);

  @Output() loginSuccessful = new EventEmitter<void>();
  @Output() toggleLoginForm = new EventEmitter<void>();

  form: FormGroup;
  requesting = false;
  email: string;
  token: string;
  resendCodeTime: number;
  private countdownTimeout: unknown;

  constructor(
    private readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    readonly authService: AuthenService,
    private readonly eventPayment: EventPaymentService,
    private readonly sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.email = this.sessionService.getCookie(StorageEnum.email);
    this.token = this.sessionService.getCookie(StorageEnum.mfaToken);
    if (!this.email || !this.token) {
      this.goLoginPage();
      return;
    }
    this.initForm();
    this.startCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownTimeout) {
      clearTimeout(this.countdownTimeout as number);
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      otp: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
      email: [this.email, [Validators.required]],
      token: [this.token, [Validators.required]],
    });
  }

  startCountdown(): void {
    this.resendCodeTime = 30;
    this.countdown();
  }

  private countdown(): void {
    if (this.resendCodeTime > 0) {
      this.countdownTimeout = setTimeout(() => {
        this.resendCodeTime--;
        this.countdown();
      }, 1000);
    }
  }

  verified(): void {
    FormUtils.markFormAsTouched(this.form);
    if (this.form.invalid) {
      this.toastService.showError('Toast.CheckMandatoryField', '', 5000);
      return;
    }

    this.requesting = true;
    const eventDateTime = StringUtils.getParamFromUrl(UrlParam.EventDateTime);
    const payload = this.form.value;

    this.authService
      .verifyTwoFactor(payload)
      .pipe(
        finalize(() => {
          this.requesting = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastService.showSuccess('UI.Toast.VerifySuccess', '', 5000);
          this.sessionService.deleteGlobalFilterCookies();

          if (this.eventId) {
            this.eventPayment.handleEventPayment({
              id: this.eventId,
              eventDateTime,
            } as EventInterface);
          } else {
            this.loginSuccessful.next();
          }
        },
        error: () => {
          this.requesting = false;
          this.toastService.showError('UI.Toast.VerifyFailed', '', 5000);
        },
      });
  }

  resendCode(): void {
    if (this.resendCodeTime > 0) {
      return;
    }

    this.requesting = true;
    const payload = this.form.value;
    this.authService
      .resendOTP(payload.email, payload.token)
      .pipe(
        finalize(() => {
          this.requesting = false;
          this.startCountdown();
        })
      )
      .subscribe({
        next: (res: Record<string, unknown>) => {
          this.toastService.showSuccess('UI.Toast.ResendCodeSuccess', '', 5000);
          this.form.patchValue({
            token: res.token,
          });
        },
        error: () => {
          this.toastService.showError('UI.Toast.ResendCodeFailed', '', 5000);
        },
      });
  }

  back(): void {
    this.toggleLoginForm.emit();
  }

  goLoginPage(): void {
    const url = environment.jipUrl.login;
    FormUtils.navigateTo(url);
  }
}
